import { Owner } from '@ltvco/refresh-lib/v1';
import { Phone, Radar, Person2, TaskAlt } from '@mui/icons-material';

type DataCounts = {
  potentialOwnersLength: number;
  searchVolumeLength: number;
  commentsLength: number;
  neighborSpoofingLength: number;
  spamReportsCount: number;
};

export const getPhoneNavLinkData = (owner: Owner, dataCounts: DataCounts) => {
  return [
    {
      href: '#phone-overview',
      sectionId: '#phone-overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
      name: 'phone-overview',
    },
    {
      href: '#phone-fraudscan-section',
      sectionId: '#phone-fraudscan-section',
      text: 'FraudScan Summary',
      reportItemsCount: null,
      hideReportItemsCount: true,
      name: 'phone-fraudscan-section',
    },
    {
      href: '#potential-owners-section',
      sectionId: '#potential-owners-section',
      text: 'Potential Owners',
      reportItemsCount: dataCounts.potentialOwnersLength,
      notificationSection: 'owners',
      name: 'potential-owners-section',
    },
    {
      href: '#possible-photos-section',
      sectionId: '#possible-photos-section',
      text: 'Possible Photos',
      reportItemsCount: owner.images?.length || null,
      notificationSection: 'photos',
      name: 'possible-photos-section',
    },
    {
      href: '#phone-numbers-section',
      sectionId: '#phone-numbers-section',
      text: 'Other Phone Numbers',
      reportItemsCount: Math.max(owner.phones.length - 1, 0),
      notificationSection: 'phones',
      name: 'phone-numbers-section',
    },
    {
      href: '#email-section',
      sectionId: '#email-section',
      text: 'Email Addresses',
      reportItemsCount: owner.emails.length,
      notificationSection: 'emails',
      name: 'email-section',
    },
    {
      href: '#address-history-section',
      sectionId: '#address-history-section',
      text: 'Address History',
      reportItemsCount: owner.addresses.length,
      notificationSection: 'addresses',
      name: 'address-history-section',
    },
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      text: 'Work History',
      reportItemsCount: owner.jobs.length,
      notificationSection: 'jobs',
      name: 'jobs-section',
    },
    {
      href: '#education-section',
      sectionId: '#education-section',
      text: 'Educational Background',
      reportItemsCount: owner.educations.length,
      notificationSection: 'educations',
      name: 'education-section',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      text: 'Social Media',
      reportItemsCount: owner.profiles.length + owner.usernames.length,
      notificationSection: 'socials',
      name: 'social-media-section',
    },
    {
      href: '#neighbor-spoofing-section',
      sectionId: '#neighbor-spoofing-section',
      text: 'Neighbor Spoofing',
      reportItemsCount: dataCounts.neighborSpoofingLength,
      notificationSection: 'neighbors',
      name: 'neighbor-spoofing-section',
    },
    {
      href: '#search-volume-section',
      sectionId: '#search-volume-section',
      text: 'Search Volume',
      reportItemsCount: dataCounts.searchVolumeLength,
      name: 'search-volume-section',
    },
    {
      href: '#nuisance-caller-section',
      sectionId: '#nuisance-caller-section',
      text: 'Nuisance Caller',
      reportItemsCount: 1,
      name: 'nuisance-caller-section',
    },
    {
      href: '#comments-section',
      sectionId: '#comments-section',
      text: 'Comments',
      reportItemsCount: dataCounts.commentsLength,
      name: 'comments-section',
    },
  ];
};

export const getPhoneNavLinkDataReorderedPhoneReportVariation = (
  owner: Owner,
  dataCounts: DataCounts
) => {
  return [
    {
      href: '#phone-overview',
      labelText: 'Overview',
      icons: [<Phone sx={{ color: '#0052FF', fontSize: '2.5rem' }} />],
      name: 'phone-overview',
    },
    {
      labelText: 'Potential Owners',
      icons: [<Person2 sx={{ color: '#0052FF', fontSize: '1.4rem' }} />],
      name: 'potential-owners',
      childItems: [
        {
          href: '#potential-owners-section',
          labelText: 'Potential Owners',
          reportItemsCount: dataCounts.potentialOwnersLength,
          notificationSection: 'owners',
          name: 'potential-owners-section',
        },
        {
          href: '#phone-numbers-section',
          labelText: 'Phone Numbers',
          reportItemsCount: Math.max(owner.phones.length - 1, 0),
          notificationSection: 'phones',
          name: 'phone-numbers-section',
        },
        {
          href: '#email-section',
          labelText: 'Email Addresses',
          reportItemsCount: owner.emails.length,
          notificationSection: 'emails',
          name: 'email-section',
        },
        {
          href: '#address-history-section',
          labelText: 'Address History',
          reportItemsCount: owner.addresses.length,
          notificationSection: 'addresses',
          name: 'address-history-section',
        },
        {
          href: '#social-media-section',
          sectionId: '#social-media-section',
          labelText: 'Social Media',
          reportItemsCount: owner.profiles.length + owner.usernames.length,
          notificationSection: 'social',
          name: 'social-media-section',
        },
        {
          href: '#jobs-section',
          sectionId: '#jobs-section',
          labelText: 'Jobs',
          reportItemsCount: owner.jobs.length,
          notificationSection: 'jobs',
          name: 'jobs-section',
        },
        {
          href: '#education-section',
          sectionId: '#education-section',
          labelText: 'Education',
          reportItemsCount: owner.educations.length,
          notificationSection: 'educations',
          name: 'education-section',
        },
      ],
    },

    {
      labelText: 'FraudScan',
      icons: [<Radar sx={{ color: '#0052FF', fontSize: '1.4rem' }} />],
      name: 'fraudscan',
      childItems: [
        {
          href: '#phone-fraudscan-section',
          labelText: 'Summary',
          hideReportItemsCount: true,
          name: 'phone-fraudscan-section',
        },
        {
          href: '#neighbor-spoofing-section',
          labelText: 'Neighbor Spoofing',
          reportItemsCount: dataCounts.neighborSpoofingLength,
          name: 'neighbor-spoofing-section',
        },
        {
          href: '#search-volume-section',
          labelText: 'Search Volume',
          reportItemsCount: dataCounts.searchVolumeLength,
          name: 'search-volume-section',
        },
        {
          href: '#nuisance-caller-section',
          labelText: 'Nuisance Caller',
          reportItemsCount: dataCounts.spamReportsCount,
          name: 'nuisance-caller-section',
        },
        {
          href: '#comments-section',
          labelText: 'Comments',
          reportItemsCount: dataCounts.commentsLength,
          name: 'comments-section',
        },
      ],
    },

    {
      labelText: 'Next Steps',
      icons: [<TaskAlt sx={{ color: '#0052FF', fontSize: '1.4rem' }} />],
      name: 'next-steps',
      childItems: [
        {
          href: '#Notes-section',
          labelText: 'Notes',
          hideReportItemsCount: true,
          name: 'notes-section',
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
          name: 'related-reports-section',
        },
        {
          href: '#report-rating',
          labelText: 'Report Rating',
          hideReportItemsCount: true,
          name: 'report-rating',
        },
      ],
    },
  ];
};
